import React, { useEffect, useMemo, useState } from 'react';
import { FiltersMap } from '@wix/bex-core';
import { usePageContext } from '@wix/bex-core/react';
import {
  DragAndDropBulkSubmit,
  DragAndDropCancel,
  DragAndDropSubmit,
} from '../DragAndDrop';
import { ToolbarCollectionState } from '../../state';
import { observer } from 'mobx-react-lite';
import type { TableProps } from '@wix/design-system';
import {
  DragAndDropContext,
  IDragAndDropContext,
  ISortableReactiveContext,
  SortableReactiveContext,
} from '@wix/wix-style-react-incubator/drag-and-drop';
import { DragAndDropAnnouncements } from '../DragAndDrop/DragAndDropAnnouncements';
import {
  dragHandleTestEvents,
  sortableItemTestEvents,
} from '../GridDragAndDropDndKit/dndTestEventHandlers';
import { useInfiniteScrollTableContext } from '../../providers';
import { addEventListener } from '@wix/bex-core/util';
import { TableDragAndDropState } from '../DragAndDrop/TableDragAndDropState';

export interface CollectionTableDragAndDropContextProps<
  T,
  F extends FiltersMap,
> {
  state: ToolbarCollectionState<T, F>;
  onSubmit?: DragAndDropSubmit<T, F>;
  onCancel?: DragAndDropCancel<T, F>;
  onBulkSubmit?: DragAndDropBulkSubmit<T, F>;
  children: React.ReactElement<
    TableProps<T>,
    React.ComponentType<TableProps<T>>
  >;
}

function _CollectionTableDragAndDropContext<
  T,
  F extends FiltersMap = FiltersMap,
>(props: CollectionTableDragAndDropContextProps<T, F>) {
  const { onSubmit, onCancel, onBulkSubmit, children } = props;
  const { type: Table, props: tableProps } = children;

  const tableState = useInfiniteScrollTableContext<T, F>();
  const { modalsContainerRef } = usePageContext();

  const [state] = useState(
    () =>
      (tableState.tableDragAndDropState ??= new TableDragAndDropState({
        tableState,
        modalsContainerRef,
      })),
  );

  const dragAndDropState = state.dragAndDrop.dnd;

  useEffect(
    () =>
      state.init({
        a11yContainer: modalsContainerRef?.current,
      }),
    [],
  );

  dragAndDropState.onSubmit = onSubmit;
  dragAndDropState.onCancel = onCancel;
  dragAndDropState.onBulkSubmit = onBulkSubmit;

  const [dragAndDropContext] = useState<IDragAndDropContext>(() => ({
    onDragAndDropDisabledRowPointerDown: (id, e) => {
      dragAndDropState.attachDisabledDragAttempt(id as string, e, 'y');
    },
    cancelDrop: (ev) => dragAndDropState.beforeDrop(ev),
    dropAnimation: {
      duration: dragAndDropState.dropAnimation.duration,
      easing: dragAndDropState.dropAnimation.easing,
    },
    announcements: dragAndDropState.nullAnnouncements,
    a11yContainer: dragAndDropState.a11yContainer,
    onDragOver: ({ over }) => dragAndDropState.onDragOver({ over }),
    onDragMove: (event: any) => dragAndDropState.onDragMove(event),
    restoreFocus: false,
    onDropAnimationEnd: (id, dragHandleRef) => {
      return addEventListener(
        dragAndDropState.events,
        'dropAnimationEnd',
        () => {
          dragAndDropState.focusDragHandleIfDropped(id, dragHandleRef.current);
        },
      );
    },
    eventHandlers: {
      row: ({ id }) => {
        const testEvents =
          process.env.NODE_ENV === 'test'
            ? sortableItemTestEvents({
                state: dragAndDropState,
                id,
              })
            : undefined;

        return {
          ...testEvents,
          onPointerDown: (e) => {
            dragAndDropState.containerEvents.onPointerDown?.(e);
            testEvents?.onPointerDown?.(e);
          },
        };
      },
      handle: ({ id }) => ({
        onPointerDown: (e) => {
          dragAndDropState.handleEvents.onPointerDown?.(e);
        },
        onKeyDown: (e) => {
          dragAndDropState.handleEvents.onKeyDown?.(e);
        },
        ...(process.env.NODE_ENV === 'test' &&
          dragHandleTestEvents({
            state: dragAndDropState,
            id,
          })),
      }),
      keyboardCodes: dragAndDropState.keyboardCodes,
    },
  }));

  dragAndDropContext.a11yContainer = dragAndDropState.a11yContainer;

  const sortableReactiveContext = useMemo<ISortableReactiveContext>(
    () => ({
      dropDisabled: dragAndDropState.isActiveRectOutsideContainer,
    }),
    [dragAndDropState.isActiveRectOutsideContainer],
  );

  return (
    <DragAndDropContext.Provider value={dragAndDropContext}>
      <SortableReactiveContext.Provider value={sortableReactiveContext}>
        <Table
          {...tableProps}
          isDragAndDropDisabled={dragAndDropState.isDisabled}
          onDragStart={(event: any) => dragAndDropState.onDragStart(event)}
          onDragEnd={(event: any) => dragAndDropState.onDragEnd(event)}
          onDragCancel={() => dragAndDropState.onDragCancel()}
        />

        <DragAndDropAnnouncements
          state={dragAndDropState}
          dataHook="collection-table-dnd-live-region"
        />
      </SortableReactiveContext.Provider>
    </DragAndDropContext.Provider>
  );
}

export const CollectionTableDragAndDropContext = observer(
  _CollectionTableDragAndDropContext,
);
